import { reportWorkflow, newAttemptId } from "../observability/telemetry.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "../Components/axiosInstance.js";
import { clearFormDraft, draftUuid, readFormDraftAsync, resourceScopedDraftId, retainFormDraftCandidate, writeFormDraft } from "../utils/formDrafts.js";

const utcTimestamp = value => new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`).getTime();
const clone = value => JSON.parse(JSON.stringify(value));
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const baseUrl = () => `${process.env.REACT_APP_API_URL || "/api/v1"}/form-drafts`;

// Each request belongs to an immutable editor identity, not whichever user signs in later.
export default function useRecoverableAutosave({ draftKey, value, dirty, versionId, target, enabled, suspendSync = false, onRecover }) {
  const [state, setState] = useState({ status: "idle", savedAt: null, error: "" });
  const valueFingerprint = JSON.stringify(value);
  const current = useRef();
  current.current = { draftKey, value, dirty, versionId, target, enabled, suspendSync, onRecover };
  const session = useRef({ key: draftKey, id: null, writerId: draftUuid(), revision: 0, initialized: false, paused: false, conflict: false, flight: null, commit: null });
  if (session.current.key !== draftKey) session.current = { key: draftKey, id: null, writerId: draftUuid(), revision: 0, initialized: false, paused: false, conflict: false, flight: null, commit: null };

  const checkpoint = useCallback(async () => {
    const ctx = current.current, run = session.current;
    if (!ctx.enabled || !ctx.dirty || run.key !== ctx.draftKey || run.committedFingerprint === JSON.stringify(ctx.value)) return;
    const checkpointValue = clone(ctx.value);
    const capability = await writeFormDraft(ctx.draftKey, ctx.value, ctx.versionId, { writerId: run.writerId, draftId: run.id, cloudRevision: run.revision, ...(run.pendingCommit ? { pendingCommit: run.pendingCommit } : {}) });
    if (session.current !== run) return;
    if (run.committedFingerprint === JSON.stringify(checkpointValue)) {
      await clearFormDraft(ctx.draftKey, { writerId: run.writerId, value: checkpointValue });
      return;
    }
    setState(old => ({ ...old, status: run.conflict ? "conflict" : run.paused ? "paused" : capability === "tab" ? "tab" : navigator.onLine === false ? "offline" : "local", savedAt: capability === "device" ? Date.now() : old.savedAt, storageWarning: capability === "tab" ? "Durable browser storage is unavailable. Keep this tab open until Save succeeds or the draft syncs." : "", error: "" }));
  }, []);

  const sync = useCallback(async (force = false, snapshotOverride = null) => {
    const ctx = current.current, run = session.current;
    if (!ctx.enabled || ctx.suspendSync || !run.initialized || run.paused || run.conflict || run.pendingCommit || (!ctx.dirty && !force)) return null;
    if (run.flight) { const result = await run.flight; return force ? sync(force, snapshotOverride) : result; }
    const synchronize = async () => {
    if (!run.contextKey) {
      try { const response = await axiosInstance.get(`${baseUrl()}/context`); if (session.current !== run) return null; run.contextKey = response.data.context_key; }
      catch { return null; }
    }
    const captured = clone(snapshotOverride || ctx.value), baseVersion = ctx.versionId;
    if (!force && equal(run.lastSynced, captured)) return null;
    const operation = async () => {
      if (navigator.onLine === false) { setState(old => ({ ...old, status: "offline" })); return null; }
      setState(old => ({ ...old, status: "saving" }));
      const mutation = draftUuid();
      try {
        const response = await axiosInstance.put(`${baseUrl()}/${run.id}`, { ...ctx.target, base_version_id: baseVersion || null, schema_version: 1, expected_revision: run.revision, mutation_id: mutation, snapshot: captured }, { headers: { "X-Form-Draft-Context": run.contextKey } });
        if (session.current !== run) return null;
        run.revision = response.data.revision;
        if (run.failures >= 3) reportWorkflow({ workflow: "draft_sync", outcome: "succeeded", reason: "completed", attemptId: newAttemptId() });
        run.failures = 0;
        run.lastSynced = captured;
        run.broadcast?.postMessage({ writerId: run.writerId, revision: run.revision });
        if (equal(current.current.value, captured)) {
          await writeFormDraft(ctx.draftKey, captured, baseVersion, { writerId: run.writerId, draftId: run.id, cloudRevision: run.revision, ...(run.pendingCommit ? { pendingCommit: run.pendingCommit } : {}) });
          setState(old => ({ ...old, status: "synced", savedAt: Date.now(), error: "" }));
        } else await checkpoint();
        return response.data;
      } catch (error) {
        if (session.current !== run) return null;
        const status = error.response?.status;
        if (status === 409) run.conflict = true;
        if (status === 401 || status === 403 || error?.errorCode === "interaction_required") run.paused = true;
        setState(old => ({ ...old, status: run.conflict ? "conflict" : run.paused ? "paused" : "offline", error: run.conflict ? "A newer draft exists. Reload to compare and restore it before saving." : "Your edits remain on this device. Draft sync will retry when the connection and session recover." }));
        run.failures = (run.failures || 0) + 1;
        if (run.failures === 3 && ![401, 403, 409].includes(status)) reportWorkflow({ workflow: "draft_sync", outcome: status ? "failed" : "unknown", reason: status ? "request_failed" : "persistence_unknown", attemptId: newAttemptId(), requestId: error.response?.headers?.["x-request-id"] });
        // A lost response may have committed. Read before any future retry.
        run.needsReconcile = true;
        return null;
      }
    };
    if (run.needsReconcile) {
      try {
        const response = await axiosInstance.get(`${baseUrl()}/${run.id}`);
        if (session.current !== run) return null;
        run.revision = response.data.revision;
        run.conflictingDraft = response.data;
        if (response.data.snapshot && !equal(response.data.snapshot, captured) && !equal(response.data.snapshot, run.lastSynced)) {
          run.conflict = true;
          setState(old => ({ ...old, status: "conflict", error: "The cloud draft differs. Reload and review both copies before saving." }));
          return null;
        }
        run.needsReconcile = false;
      } catch (error) { if (error.response?.status !== 404) return null; run.needsReconcile = false; }
    }
    return operation();
    };
    run.flight = synchronize();
    try { return await run.flight; } finally { run.flight = null; }
  }, [checkpoint]);

  useEffect(() => {
    if (!enabled) return;
    const run = session.current, ctx = current.current;
    setState({ status: "idle", savedAt: null, error: "" });
    let cancelled = false;
    (async () => {
      const local = await readFormDraftAsync(draftKey);
      run.id = local?.draftId || draftUuid(); run.revision = local?.cloudRevision || 0;
      run.pendingCommit = local?.pendingCommit || null;
      run.sourceCandidate = local?.writerId ? { writerId: local.writerId, savedAt: local.savedAt, storageKey: local.storageKey, value: local.value } : null;
      try {
        const contextResponse = await axiosInstance.get(`${baseUrl()}/context`);
        if (cancelled || session.current !== run) return;
        run.contextKey = contextResponse.data.context_key;
        if (!local?.draftId && ctx.target.resource_id) run.id = await resourceScopedDraftId(run.contextKey, ctx.target);
        if (cancelled || session.current !== run) return;
        let cloud;
        if (local?.draftId) cloud = (await axiosInstance.get(`${baseUrl()}/${run.id}`)).data;
        else {
          if (ctx.target.resource_id) {
            try { cloud = (await axiosInstance.get(`${baseUrl()}/${run.id}`)).data; }
            catch (error) { if (error.response?.status !== 404) throw error; }
          }
          if (!cloud) {
            const rows = (await axiosInstance.get(baseUrl())).data;
            cloud = rows.find(row => row.kind === ctx.target.kind && row.resource_id === ctx.target.resource_id && row.farm_id === ctx.target.farm_id && row.field_id === ctx.target.field_id);
          }
        }
        if (cancelled || session.current !== run) return;
        if (cloud) {
          run.id = cloud.id; run.revision = cloud.revision;
          if (cloud.snapshot && !local) {
            if (current.current.dirty) await checkpoint();
            const candidate = await retainFormDraftCandidate(ctx.draftKey, cloud.snapshot, cloud.base_version_id, { draftId: cloud.id, cloudRevision: cloud.revision, savedAt: utcTimestamp(cloud.updated_at) });
            const recovery = await readFormDraftAsync(ctx.draftKey);
            if (cancelled || session.current !== run) return;
            ctx.onRecover?.({ ...candidate, alternatives: recovery?.alternatives || [candidate] });
          }
          else if (cloud.snapshot && local && !equal(cloud.snapshot, local.value) && (local.cloudRevision || 0) < cloud.revision) {
            run.conflict = true;
            run.conflictingDraft = cloud;
            setState({ status: "conflict", savedAt: local.savedAt, error: "A newer cloud draft is available. Reload or discard your candidate before overwriting it." });
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) run.needsReconcile = Boolean(local?.draftId);
      } finally { if (!cancelled && session.current === run) { run.initialized = true; if (ctx.dirty) checkpoint(); } }
    })();
    return () => { cancelled = true; };
  }, [draftKey, enabled, checkpoint]);

  useEffect(() => {
    if (!enabled || !dirty) return;
    const localTimer = setTimeout(checkpoint, 250);
    const cloudTimer = setTimeout(() => sync(), 2000);
    return () => { clearTimeout(localTimer); clearTimeout(cloudTimer); };
  }, [valueFingerprint, dirty, enabled, suspendSync, checkpoint, sync]);

  useEffect(() => {
    const pause = () => { session.current.paused = true; setState(old => ({ ...old, status: "paused" })); };
    const resume = () => { session.current.paused = false; sync(); };
    const foreground = () => { if (document.visibilityState !== "hidden") sync(); };
    const maxWait = setInterval(() => sync(), 10000);
    window.addEventListener("fieldprint:session-expired", pause);
    window.addEventListener("fieldprint:session-recovered", resume);
    window.addEventListener("fieldprint:checkpoint-drafts", checkpoint);
    const reconnect = () => sync();
    window.addEventListener("online", reconnect);
    window.addEventListener("pagehide", checkpoint);
    const beforeUnload = event => { if (current.current.enabled && current.current.dirty) { checkpoint(); event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("visibilitychange", foreground);
    const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(`draft:${draftKey}`) : null;
    const run = session.current;
    run.broadcast = channel;
    if (channel) channel.onmessage = event => {
      if (session.current === run && event.data?.writerId !== run.writerId) run.needsReconcile = true;
    };
    return () => { clearInterval(maxWait); window.removeEventListener("fieldprint:session-expired", pause); window.removeEventListener("fieldprint:session-recovered", resume); window.removeEventListener("fieldprint:checkpoint-drafts", checkpoint); window.removeEventListener("online", reconnect); window.removeEventListener("pagehide", checkpoint); window.removeEventListener("beforeunload", beforeUnload); document.removeEventListener("visibilitychange", foreground); channel?.close(); };
  }, [draftKey, sync, checkpoint]);

  const commit = useCallback(async (payload, assessment = {}, snapshotOverride = null) => {
    const run = session.current, ctx = current.current;
    if (run.commit) return run.commit;
    if (!ctx.enabled || ctx.suspendSync) throw new Error("Choose Restore or Discard before saving this record.");
    if (run.paused || run.conflict) throw new Error("Restore your session or resolve the newer draft before saving.");
    const captured = clone(snapshotOverride || ctx.value);
    const execute = async () => {
      await checkpoint();
      if (run.pendingCommit) {
        const replay = await axiosInstance.post(`${baseUrl()}/${run.id}/commit`, run.pendingCommit.request, { headers: { "X-Form-Draft-Context": run.contextKey } });
        if (session.current !== run) throw new Error("Editor context changed; reload before continuing.");
        const committedSnapshot = run.pendingCommit.captured;
        run.pendingCommit = null;
        run.revision = replay.data.draft.revision;
        const hasNewerEdits = !equal(current.current.value, committedSnapshot);
        if (!hasNewerEdits) run.committedFingerprint = JSON.stringify(committedSnapshot);
        if (!hasNewerEdits) {
          await clearFormDraft(ctx.draftKey, { writerId: run.writerId, value: committedSnapshot });
          if (run.sourceCandidate?.writerId !== run.writerId) await clearFormDraft(run.sourceCandidate?.storageKey || ctx.draftKey, run.sourceCandidate);
        }
        else await writeFormDraft(ctx.draftKey, current.current.value, replay.data.record.current_version_id, { writerId: run.writerId, draftId: run.id, cloudRevision: run.revision });
        return { data: replay.data.record, hasNewerEdits };
      }
      const draft = await sync(true, captured);
      if (!draft || session.current !== run) throw new Error("Draft sync has not finished. Your edits remain on this device; retry Save.");
      const mutation = run.pendingCommit?.mutation || draftUuid();
      const request = run.pendingCommit?.request || { expected_revision: run.revision, mutation_id: mutation, payload, ...assessment };
      run.pendingCommit = { mutation, request, captured };
      await writeFormDraft(ctx.draftKey, current.current.value, ctx.versionId, { writerId: run.writerId, draftId: run.id, cloudRevision: run.revision, pendingCommit: run.pendingCommit });
      const response = await axiosInstance.post(`${baseUrl()}/${run.id}/commit`, request, { headers: { "X-Form-Draft-Context": run.contextKey } });
      if (session.current !== run) throw new Error("The editor context changed. Reload the saved record before continuing.");
      run.pendingCommit = null;
      run.revision = response.data.draft.revision;
      run.broadcast?.postMessage({ writerId: run.writerId, revision: run.revision });
      const hasNewerEdits = !equal(current.current.value, captured);
      if (!hasNewerEdits) run.committedFingerprint = JSON.stringify(captured);
      if (!hasNewerEdits) {
          await clearFormDraft(ctx.draftKey, { writerId: run.writerId, value: captured });
          if (run.sourceCandidate?.writerId !== run.writerId) await clearFormDraft(run.sourceCandidate?.storageKey || ctx.draftKey, run.sourceCandidate);
        }
      else await writeFormDraft(ctx.draftKey, current.current.value, response.data.record.current_version_id, { writerId: run.writerId, draftId: run.id, cloudRevision: run.revision, ...(run.pendingCommit ? { pendingCommit: run.pendingCommit } : {}) });
      setState({ status: hasNewerEdits ? "local" : "idle", savedAt: Date.now(), error: "" });
      return { data: response.data.record, hasNewerEdits };
    };
    run.commit = execute();
    try {
      const result = await run.commit;
      reportWorkflow({ workflow: "draft_commit", outcome: "succeeded", reason: "completed", attemptId: newAttemptId() });
      return result;
    } catch (error) {
      if (error.response && error.response.status >= 400 && error.response.status < 500 && session.current === run) {
        run.pendingCommit = null;
        if (error.response.status === 409) run.conflict = true;
        await checkpoint();
      }
      if (run.pendingCommit && !error.response) reportWorkflow({ workflow: "draft_commit", outcome: "unknown", reason: "persistence_unknown", attemptId: newAttemptId() });
      throw error;
    } finally { run.commit = null; }
  }, [checkpoint, sync]);

  const discard = useCallback(async (selected = null) => {
    const run = session.current;
    const local = selected || await readFormDraftAsync(current.current.draftKey);
    run.id ||= local?.draftId;
    run.revision ||= local?.cloudRevision || 0;
    await clearFormDraft(local?.storageKey || current.current.draftKey, local?.writerId ? { writerId: local.writerId, savedAt: local.savedAt, storageKey: local.storageKey, value: local.value } : null);
    if (run.id && run.revision && (!selected || selected.cloudRevision)) {
      try { const response = await axiosInstance.delete(`${baseUrl()}/${run.id}`, { params: { revision: local?.cloudRevision || run.revision }, headers: { "X-Form-Draft-Context": run.contextKey } }); run.revision = response.data.revision; }
      catch { setState(old => ({ ...old, status: "conflict", error: "Local candidate discarded; cloud draft could not be discarded. Reload to review it." })); return; }
    }
    if (run.sourceCandidate?.writerId === local?.writerId) run.sourceCandidate = null;
    run.conflict = false;
    run.broadcast?.postMessage({ writerId: run.writerId, revision: run.revision });
    setState({ status: "idle", savedAt: null, error: "" });
  }, []);
  const reviewCloud = useCallback(async () => {
    const run = session.current;
    const response = await axiosInstance.get(`${baseUrl()}/${run.id}`);
    if (session.current !== run || !response.data.snapshot) return;
    run.revision = response.data.revision;
    run.conflict = false;
    run.needsReconcile = false;
    const candidate = await retainFormDraftCandidate(current.current.draftKey, response.data.snapshot, response.data.base_version_id, { draftId: run.id, cloudRevision: run.revision, savedAt: utcTimestamp(response.data.updated_at) });
    const recovery = await readFormDraftAsync(current.current.draftKey);
    if (session.current !== run) return;
    current.current.onRecover?.({ ...candidate, alternatives: recovery?.alternatives || [candidate] });
    setState(old => ({ ...old, status: "local", error: "Review the cloud draft with Restore, or discard it. Your original local candidate remains until you choose." }));
  }, []);
  const keepLocal = useCallback(async () => {
    const run = session.current;
    const response = await axiosInstance.get(`${baseUrl()}/${run.id}`);
    if (session.current !== run) return;
    run.revision = response.data.revision;
    run.conflict = false;
    run.needsReconcile = false;
    run.lastSynced = null;
    await checkpoint();
    await sync();
  }, [checkpoint, sync]);
  const selectRecovery = useCallback(selected => {
    session.current.sourceCandidate = selected?.writerId ? { writerId: selected.writerId, savedAt: selected.savedAt, storageKey: selected.storageKey, value: selected.value } : null;
  }, []);
  return { ...state, checkpoint, commit, discard, reviewCloud, keepLocal, selectRecovery };
}
